import { FC, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

import { useRecoilValue } from 'recoil';

import GiftItemPreview from '@service/giftStore/components/GiftItemPreview';
import { GiftChoiceUnitData } from '@shared/api/giftChoiceUnit/giftChoiceUnit.interface';
import { giftDeliveryCategoryListDataAtom } from '@shared/api/giftDeliveryCategory/giftDeliveryCategory.atom';
import { GiftDeliveryCategoryData } from '@shared/api/giftDeliveryCategory/giftDeliveryCategory.interface';
import { useGiftDeliveryCategoryListHook } from '@shared/api/giftDeliveryCategory/giftDeliveryCategory.hook';
import { trackGiftEvent } from '@shared/api/giftEventLog/giftEventLog.controller';
import {
  giftItemListForStoreDataAtomFamily,
  giftItemListForStoreStatusAtomFamily,
} from '@shared/api/giftItem/giftItem.atom';
import { useGiftItemListForStoreHook } from '@shared/api/giftItem/giftItem.hook';
import { GiftItemForStore } from '@shared/api/giftItem/giftItem.interface';
import { GiftItemInfoType } from '@shared/api/giftItemInfo/giftItemInfo.interface';
import { Image } from '@shared/api/media/media.interface';
import { Loading } from '@shared/components/Loading';
import { StoreUserMenuButton } from '@shared/components/StoreUserMenuButton';
import { useRedirect } from '@shared/hooks/useRedirect';
import { LowerRole } from '@shared/types';
import { fileBucketUrl } from '@shared/utils/fileUtils';
import {
  arrangeGiftBestFirst,
  withoutGiftBestTag,
} from '@shared/utils/giftBestTag.util';
import { Link, useNavigationType, useSearchParams } from 'react-router-dom';
import giftPromStoreMain from 'src/assets/gift/gift-prom-store-main.png';

import { GiftCallHelperBox } from '../components/GiftCallHelperBox';
import GiftStoreFooter from '../components/GiftStoreFooter';

type DeliverySection = {
  category: GiftDeliveryCategoryData;
  groups: { sub: string; items: GiftItemForStore[] }[];
  ungrouped: GiftItemForStore[];
  count: number;
};

// 상단 점프 내비에 항상 전부 노출되는 칩 (하위분류 또는 하위분류 없는 카테고리)
type DeliveryChip = {
  key: string; // `sub:<id>:<name>` | `cat:<id>` | `etc`
  label: string;
  count: number;
};

const GiftStoreMainPage: FC = function GiftStoreMainPage() {
  const navigationType = useNavigationType();
  const [searchParams, setSearchParams] = useSearchParams();
  const bugoId = searchParams.get('bugoId');

  const method = searchParams.get('method');
  const fromAlimTalk = method === 'kakao';

  useRedirect({ skip: !fromAlimTalk });

  useEffect(() => {
    if (fromAlimTalk) {
      const nextParams = new URLSearchParams(searchParams);
      nextParams.delete('method');
      setSearchParams(nextParams, { replace: true });
    }
  }, [fromAlimTalk, searchParams, setSearchParams]);

  const paramsString = searchParams.toString();

  // bugoId 없음 / 발인 후 30일 경과는 GiftStorePurchaseGate가 안내 화면으로 처리하므로
  // 이 페이지는 항상 유효한 bugoId 상태에서만 렌더된다.
  useEffect(() => {
    if (bugoId) trackGiftEvent('store_view', bugoId);
  }, [bugoId]);

  useGiftItemListForStoreHook(bugoId ?? '');
  const status = useRecoilValue(giftItemListForStoreStatusAtomFamily(bugoId ?? ''));
  const data = useRecoilValue(giftItemListForStoreDataAtomFamily(bugoId ?? ''));

  useGiftDeliveryCategoryListHook(LowerRole.Public);
  const deliveryCategoryList = useRecoilValue(giftDeliveryCategoryListDataAtom);
  const getImageUrl = (item: GiftItemForStore): string => {
    const image = item.image || item.giftItemInfo?.image;
    if (image && typeof image === 'object' && 'url' in image) {
      return fileBucketUrl((image as Image).url, 'w400');
    }
    return '';
  };

  const getName = (item: GiftItemForStore): string => {
    return item.name || item.giftItemInfo?.name || '';
  };

  const getType = (item: GiftItemForStore): GiftItemInfoType => {
    return item.giftItemInfo?.type || GiftItemInfoType.Delivery;
  };

  // BEST 태그는 배지로 따로 보여주므로 일반 태그 줄에서는 뺀다
  const getTags = (item: GiftItemForStore): string[] => {
    return withoutGiftBestTag(item.giftItemInfo?.tags);
  };

  const getIsSelectable = (item: GiftItemForStore): boolean => {
    return item.giftItemInfo?.isSelectable || false;
  };

  const getChoiceUnitCount = (item: GiftItemForStore): number => {
    const units = item.giftItemInfo?.choiceUnits;
    if (!units || !Array.isArray(units)) return 0;
    return units.filter(
      (u): u is GiftChoiceUnitData => typeof u === 'object' && u !== null,
    ).length;
  };

  const getBrandName = (item: GiftItemForStore): string | undefined => {
    const brand = item.giftItemInfo?.giftItemBrand;
    if (brand && typeof brand === 'object' && 'name' in brand) {
      return brand.name;
    }
    return undefined;
  };

  const getDisplayOrder = (item: GiftItemForStore): number => {
    if (item.order !== undefined) return item.order;
    if (item.giftItemInfo?.order !== undefined) return item.giftItemInfo.order;
    return Number.MAX_SAFE_INTEGER;
  };

  const getCategoryId = (item: GiftItemForStore): string | undefined => {
    const override = item.giftDeliveryCategory;
    if (override) {
      return typeof override === 'object' ? override._id : override;
    }
    const fromInfo = item.giftItemInfo?.giftDeliveryCategory;
    if (fromInfo) {
      return typeof fromInfo === 'object' ? fromInfo._id : fromInfo;
    }
    return undefined;
  };

  // 상품이 선택한 하위분류 이름 (원상품에만 존재)
  const getSubCategory = (item: GiftItemForStore): string | undefined => {
    return item.giftItemInfo?.subCategory || undefined;
  };

  const sortedData = useMemo(() => {
    if (!data) return null;
    return [...data]
      .filter((item) => item.giftItemInfo?.isEnabled !== false)
      .sort((a, b) => {
        const typeA = getType(a) === GiftItemInfoType.Coupon ? 0 : 1;
        const typeB = getType(b) === GiftItemInfoType.Coupon ? 0 : 1;
        if (typeA !== typeB) return typeA - typeB;
        return getDisplayOrder(a) - getDisplayOrder(b);
      });
  }, [data]);

  // 상세 → 뒤로가기 시 보던 스크롤 위치 복원용 키
  const scrollStorageKey = `giftStoreScroll:${bugoId ?? ''}`;

  // 상세 페이지 등으로 떠날 때 현재 스크롤 위치를 저장한다.
  // (ScrollToTop이 POP에서 맨 위로 보내지 않으므로 복원 가능)
  useEffect(() => {
    return () => {
      sessionStorage.setItem(scrollStorageKey, String(window.scrollY));
    };
  }, [scrollStorageKey]);

  // 뒤로가기(POP)로 돌아와 목록이 준비되면 저장된 위치로 복원한다.
  useLayoutEffect(() => {
    if (navigationType !== 'POP') return;
    if (status !== 'success' || !sortedData) return;
    const saved = sessionStorage.getItem(scrollStorageKey);
    if (saved === null) return;
    window.scrollTo(0, Number(saved));
    sessionStorage.removeItem(scrollStorageKey);
  }, [navigationType, status, sortedData, scrollStorageKey]);

  // 쿠폰형/배송형 섹션 시작점 (앵커 탭 점프 + 스크롤 스파이 대상)
  const couponSectionRef = useRef<HTMLElement>(null);
  const deliverySectionRef = useRef<HTMLElement>(null);
  // 배송형 점프 타깃 ref. 키 형식: `cat:<id>` | `sub:<id>:<name>` | `etc`
  const sectionRefs = useRef<Record<string, HTMLElement | null>>({});
  // 하위분류 칩 줄(가로 스크롤 컨테이너)과 각 칩 버튼 ref — 활성 칩을 보이게 스크롤
  const chipScrollRef = useRef<HTMLDivElement>(null);
  const chipRefs = useRef<Record<string, HTMLButtonElement | null>>({});

  const scrollToTop = (el: HTMLElement | null) => {
    if (!el) return;
    requestAnimationFrame(() => {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  };

  const scrollToTarget = (key: string) => {
    const el = sectionRefs.current[key];
    if (!el) return;
    requestAnimationFrame(() => {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  };

  // 쿠폰형·배송형은 한 스크롤에 이어서 노출하고, 탭은 필터가 아닌 앵커로 동작한다.
  // activeType은 스크롤 위치를 따라가는 하이라이트 (기존 tab 파라미터로 초기화).
  const [activeType, setActiveType] = useState<'Coupon' | 'Delivery'>(() =>
    searchParams.get('tab') === 'Delivery' ? 'Delivery' : 'Coupon',
  );

  const jumpToType = (next: 'Coupon' | 'Delivery') => {
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set('tab', next);
    setSearchParams(nextParams, { replace: true });
    scrollToTop(
      next === 'Coupon' ? couponSectionRef.current : deliverySectionRef.current,
    );
  };

  // 점프 내비에서 현재 보고 있는 섹션 키 (스크롤 추적 하이라이트용).
  // 키 형식: `cat:<id>` | `sub:<id>:<name>` | `etc`
  const [activeKey, setActiveKey] = useState<string | null>(null);

  const typeCounts = useMemo(() => {
    if (!sortedData) return { Coupon: 0, Delivery: 0 };
    return {
      Coupon: sortedData.filter((item) => getType(item) === GiftItemInfoType.Coupon)
        .length,
      Delivery: sortedData.filter((item) => getType(item) === GiftItemInfoType.Delivery)
        .length,
    };
  }, [sortedData]);

  const hasCoupon = typeCounts.Coupon > 0;
  const hasDelivery = typeCounts.Delivery > 0;
  const hasBothTypes = hasCoupon && hasDelivery;

  // 쿠폰형 그리드용 데이터 (배송형은 아래 deliveryGrouping이 담당)
  const couponItems = useMemo(() => {
    if (!sortedData) return [];
    return sortedData.filter((item) => getType(item) === GiftItemInfoType.Coupon);
  }, [sortedData]);

  // 배송형 상품이 실제로 존재하는 카테고리 id 집합
  const usedCategoryIds = useMemo(() => {
    const ids = new Set<string>();
    (sortedData ?? []).forEach((item) => {
      if (getType(item) !== GiftItemInfoType.Delivery) return;
      const id = getCategoryId(item);
      if (id) ids.add(id);
    });
    return ids;
  }, [sortedData]);

  // 배송형 상품이 있는 카테고리만 (order 정렬은 fetch 시점에 적용됨)
  const categoryOptions = useMemo(() => {
    return deliveryCategoryList.filter((c) => usedCategoryIds.has(c._id));
  }, [deliveryCategoryList, usedCategoryIds]);

  // 배송형: 카테고리 → 하위분류 섹션 그룹.
  // 필터로 걸러내지 않고 전 상품을 섹션으로 펼쳐서 한 스크롤에 노출한다.
  const deliveryGrouping = useMemo(() => {
    const empty = {
      sections: [] as DeliverySection[],
      uncategorized: [] as GiftItemForStore[],
      chips: [] as DeliveryChip[],
    };
    if (!sortedData) return empty;
    const deliveryItems = sortedData.filter(
      (item) => getType(item) === GiftItemInfoType.Delivery,
    );
    const coveredIds = new Set(categoryOptions.map((c) => c._id));
    const sections: DeliverySection[] = categoryOptions.map((category) => {
      const items = deliveryItems.filter((item) => getCategoryId(item) === category._id);
      const subNames = category.subCategories ?? [];
      const usedSubs = subNames.filter((name) =>
        items.some((item) => getSubCategory(item) === name),
      );
      const groups = usedSubs.map((sub) => ({
        sub,
        items: items.filter((item) => getSubCategory(item) === sub),
      }));
      // 하위분류 미지정이거나 카테고리에 없는 하위분류 상품
      const ungrouped = items.filter((item) => {
        const sub = getSubCategory(item);
        return !sub || !usedSubs.includes(sub);
      });
      return { category, groups, ungrouped, count: items.length };
    });
    // 카테고리가 없거나 노출 카테고리에 속하지 않는 배송 상품 (누락 방지)
    const uncategorized = deliveryItems.filter((item) => {
      const id = getCategoryId(item);
      return !id || !coveredIds.has(id);
    });

    // 점프 내비 칩: 모든 하위분류를 펼쳐서 한 줄에 노출.
    // 하위분류가 없는 카테고리는 카테고리명 칩으로, 미분류는 '기타' 칩으로.
    const chips: DeliveryChip[] = [];
    sections.forEach(({ category, groups, count }) => {
      if (groups.length > 0) {
        groups.forEach(({ sub, items }) =>
          chips.push({
            key: `sub:${category._id}:${sub}`,
            label: sub,
            count: items.length,
          }),
        );
      } else {
        chips.push({ key: `cat:${category._id}`, label: category.name, count });
      }
    });
    if (uncategorized.length > 0) {
      chips.push({ key: 'etc', label: '기타', count: uncategorized.length });
    }

    return { sections, uncategorized, chips };
  }, [sortedData, categoryOptions]);

  const deliverySections = deliveryGrouping.sections;
  const uncategorizedDelivery = deliveryGrouping.uncategorized;
  const deliveryChips = deliveryGrouping.chips;

  // 배송형 상품이 있으면 첫 칩을 활성으로 초기화
  useEffect(() => {
    if (!hasDelivery) return;
    setActiveKey((prev) => prev ?? deliveryChips[0]?.key ?? null);
  }, [hasDelivery, deliveryChips]);

  // 스크롤에 따라 현재 보고 있는 섹션을 추적해 칩/카테고리 내비를 하이라이트.
  // 타입 앵커 탭이 있으면 스티키 스택(탭 56px + 칩 48px)만큼 상단 여백을 더 준다.
  useEffect(() => {
    if (!hasDelivery || deliveryChips.length === 0) return;
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        const key = (visible[0]?.target as HTMLElement | undefined)?.dataset.targetKey;
        if (key) setActiveKey(key);
      },
      {
        rootMargin: hasBothTypes ? '-128px 0px -55% 0px' : '-72px 0px -55% 0px',
        threshold: 0,
      },
    );
    deliveryChips.forEach(({ key }) => {
      const el = sectionRefs.current[key];
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, [hasDelivery, hasBothTypes, deliveryChips]);

  // 스크롤 위치에 따라 쿠폰형/배송형 앵커 탭 하이라이트를 따라가게 한다.
  // getBoundingClientRect는 강제 레이아웃 읽기라 rAF로 프레임당 1회만 계산.
  useEffect(() => {
    if (!hasBothTypes) return;
    let raf = 0;
    const onScroll = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = 0;
        const el = deliverySectionRef.current;
        if (!el) return;
        setActiveType(el.getBoundingClientRect().top <= 80 ? 'Delivery' : 'Coupon');
      });
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => {
      window.removeEventListener('scroll', onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [hasBothTypes]);

  // tab=Delivery로 진입하면(공유 링크 등) 배송형 섹션에서 시작하도록 점프.
  // 뒤로가기(POP)는 위의 스크롤 위치 복원이 담당하므로 건너뛴다.
  const initialJumpDoneRef = useRef(false);
  useLayoutEffect(() => {
    if (initialJumpDoneRef.current) return;
    if (status !== 'success' || !sortedData) return;
    initialJumpDoneRef.current = true;
    if (navigationType === 'POP') return;
    if (hasBothTypes && searchParams.get('tab') === 'Delivery') {
      deliverySectionRef.current?.scrollIntoView({ block: 'start' });
    }
  }, [status, sortedData, navigationType, hasBothTypes, searchParams]);

  // 활성 칩이 속한 카테고리 id (원형 카테고리 내비 하이라이트용)
  const activeCategoryId = useMemo(() => {
    if (!activeKey) return null;
    if (activeKey.startsWith('cat:')) return activeKey.slice(4);
    if (activeKey.startsWith('sub:')) {
      const rest = activeKey.slice(4);
      const sep = rest.indexOf(':');
      return sep === -1 ? rest : rest.slice(0, sep);
    }
    return null;
  }, [activeKey]);

  // 활성 칩이 칩 줄 밖(오른쪽 등)에 있으면 줄을 가로 스크롤해 보이게 한다.
  useEffect(() => {
    if (!hasDelivery || !activeKey) return;
    const container = chipScrollRef.current;
    const el = chipRefs.current[activeKey];
    if (!container || !el) return;
    const cRect = container.getBoundingClientRect();
    const eRect = el.getBoundingClientRect();
    if (eRect.left < cRect.left || eRect.right > cRect.right) {
      const delta =
        eRect.left - cRect.left - (container.clientWidth - el.clientWidth) / 2;
      container.scrollTo({ left: container.scrollLeft + delta, behavior: 'smooth' });
    }
  }, [activeKey, hasDelivery]);

  // 스티키 스택(앵커 탭 56px + 칩 줄 48px) 유무에 따라 점프 오프셋이 달라진다
  const chipStickyTopClass = hasBothTypes ? 'top-14' : 'top-0';
  const sectionScrollMtClass = hasBothTypes ? 'scroll-mt-28' : 'scroll-mt-16';

  // 한 구역(하위분류 단위) 그리드. 배송형은 그 구역의 BEST(최대 6개, 가격 낮은 순)를 맨 앞에 놓고
  // 나머지를 기존 표시 순서대로 잇는다. 쿠폰형은 BEST 규칙 없이 그대로.
  const renderGrid = (items: GiftItemForStore[], keyPrefix: string, withBest = false) => {
    const { best, rest } = withBest
      ? arrangeGiftBestFirst(
          items,
          (item) => item.giftItemInfo?.tags,
          (item) => item.priceRetail || 0,
        )
      : { best: [] as GiftItemForStore[], rest: items };
    const bestCount = best.length;
    return (
      <div className="grid grid-cols-2 gap-2.5">
        {[...best, ...rest].map((item, idx) => (
          <Link
            key={`${keyPrefix}-${item._id}`}
            to={`/gift-store/item/${item._id}?${paramsString}`}
            state={{ name: getName(item) }}
          >
            <GiftItemPreview
              image={getImageUrl(item)}
              name={getName(item)}
              priceRegular={item.priceRegular}
              priceRetail={item.priceRetail}
              type={getType(item)}
              tags={getTags(item)}
              isBest={idx < bestCount}
              isSelectable={getIsSelectable(item)}
              choiceUnitCount={getChoiceUnitCount(item)}
              brandName={getBrandName(item)}
            />
          </Link>
        ))}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <StoreUserMenuButton floatingTopClassName="top-16" />
      {/* 히어로 배너 */}
      <div>
        <img src={giftPromStoreMain} alt="답례품 안내" className="w-full" />
      </div>

      {/* 상품 목록 */}
      {status === 'success' ? (
        sortedData && sortedData.length > 0 ? (
          <div className="px-3 pb-4 pt-3">
            {/* 쿠폰형/배송형 앵커 탭 (스티키, 탭하면 해당 섹션으로 스크롤) */}
            {hasBothTypes && (
              <div className="sticky top-0 z-30 -mx-3 mb-3 bg-gray-50 px-3 py-2">
                <div className="overflow-hidden rounded-full bg-gray-200 shadow-sm ring-1 ring-gray-200">
                  <div className="grid grid-cols-2 gap-px">
                    {[
                      { key: 'Coupon' as const, label: '쿠폰형' },
                      { key: 'Delivery' as const, label: '배송형' },
                    ].map((tab) => (
                      <button
                        key={tab.key}
                        onClick={() => jumpToType(tab.key)}
                        className={`py-2.5 text-sm font-semibold transition-colors ${
                          activeType === tab.key
                            ? 'bg-gray-900 text-white'
                            : 'bg-white text-gray-500 active:bg-gray-50'
                        }`}
                      >
                        {tab.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* 쿠폰형 섹션 */}
            {hasCoupon && (
              <section ref={couponSectionRef} className="scroll-mt-16">
                {hasBothTypes && (
                  <div className="mb-3 flex items-baseline gap-2">
                    <h2 className="text-base font-bold text-gray-900">쿠폰형 답례품</h2>
                    <span className="text-xs text-gray-400">
                      모바일 쿠폰으로 바로 전송
                    </span>
                  </div>
                )}
                {renderGrid(couponItems, 'coupon')}
              </section>
            )}

            {/* 쿠폰형 → 배송형 전환 밴드 (커머스식 섹션 구분) */}
            {hasBothTypes && <div className="-mx-3 my-6 h-2 bg-gray-100" />}

            {/* 배송형 섹션 (쿠폰형 아래에 이어서 노출) */}
            {hasDelivery && (
              <section ref={deliverySectionRef} className="scroll-mt-16">
                {hasBothTypes && (
                  <div className="mb-3 flex items-baseline gap-2">
                    <h2 className="text-base font-bold text-gray-900">배송형 답례품</h2>
                    <span className="text-xs text-gray-400">
                      원하는 주소로 배송해 드려요
                    </span>
                  </div>
                )}

                {/* 카테고리 쇼케이스 (브랜드 이미지, 탭하면 섹션으로 점프) */}
                {deliverySections.length > 1 && (
                  <div className="-mx-3 mb-3">
                    <div className="overflow-x-auto hide-scrollbar">
                      <div className="flex min-w-full justify-evenly gap-4 px-3 pb-1 pt-1">
                        {deliverySections.map(({ category }) => {
                          const isActive = activeCategoryId === category._id;
                          const imageUrl =
                            category.image && typeof category.image === 'object'
                              ? fileBucketUrl(category.image.url, 'raw')
                              : '';
                          return (
                            <button
                              key={category._id}
                              onClick={() => scrollToTarget(`cat:${category._id}`)}
                              className="flex w-[4.5rem] shrink-0 flex-col items-center gap-1.5"
                            >
                              <div
                                className={`flex h-[4.5rem] w-[4.5rem] items-center justify-center overflow-hidden rounded-full bg-gray-100 transition-all ${
                                  isActive ? 'ring-2 ring-offset-2' : ''
                                }`}
                              >
                                {imageUrl ? (
                                  <img
                                    src={imageUrl}
                                    alt={category.name}
                                    className="h-full w-full object-cover"
                                  />
                                ) : (
                                  <span className="h-full w-full bg-gray-100" />
                                )}
                              </div>
                              <span
                                className={`truncate text-13 font-semibold ${
                                  isActive ? 'theme-text-main' : 'text-gray-800'
                                }`}
                              >
                                {category.name}
                              </span>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                )}

                {/* 하위분류 칩 (전부 노출, 앵커 탭 아래 스티키, 탭하면 해당 분류로 점프) */}
                {deliveryChips.length > 1 && (
                  <div
                    className={`sticky ${chipStickyTopClass} z-20 -mx-3 mb-4 border-b border-gray-100 bg-gray-50 px-3`}
                  >
                    <div ref={chipScrollRef} className="overflow-x-auto hide-scrollbar">
                      <div className="flex min-w-full gap-2 py-2">
                        {deliveryChips.map((chip) => {
                          const isActive = activeKey === chip.key;
                          return (
                            <button
                              key={chip.key}
                              ref={(el) => {
                                chipRefs.current[chip.key] = el;
                              }}
                              onClick={() => {
                                setActiveKey(chip.key);
                                scrollToTarget(chip.key);
                              }}
                              className={`flex shrink-0 items-center gap-1 rounded-full px-3.5 py-1.5 text-13 font-semibold transition-colors ${
                                isActive
                                  ? 'text-white theme-bg-main'
                                  : 'bg-gray-100 text-gray-600'
                              }`}
                            >
                              {chip.label}
                              <span
                                className={`text-xs font-normal ${
                                  isActive ? 'text-white/80' : 'text-gray-400'
                                }`}
                              >
                                {chip.count}
                              </span>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                )}

                {/* 카테고리 → 하위분류 섹션 (전 상품을 한 스크롤에 노출) */}
                <div className="space-y-7">
                  {deliverySections.map(({ category, groups, ungrouped }) => (
                    <section
                      key={category._id}
                      data-target-key={`cat:${category._id}`}
                      ref={(el) => {
                        sectionRefs.current[`cat:${category._id}`] = el;
                      }}
                      className={sectionScrollMtClass}
                    >
                      <h2 className="mb-3 text-base font-bold text-gray-900">
                        {category.name}
                      </h2>
                      {ungrouped.length > 0 && (
                        <div className="mb-5">
                          {renderGrid(ungrouped, `cat-${category._id}-ungrouped`, true)}
                        </div>
                      )}
                      <div className="space-y-5">
                        {groups.map(({ sub, items }) => (
                          <div
                            key={sub}
                            data-target-key={`sub:${category._id}:${sub}`}
                            ref={(el) => {
                              sectionRefs.current[`sub:${category._id}:${sub}`] = el;
                            }}
                            className={sectionScrollMtClass}
                          >
                            <h3 className="mb-2.5 flex items-center gap-1.5 text-sm font-semibold text-gray-500">
                              {sub}
                              <span className="text-xs font-normal text-gray-400">
                                {items.length}
                              </span>
                            </h3>
                            {renderGrid(items, `sub-${category._id}-${sub}`, true)}
                          </div>
                        ))}
                      </div>
                    </section>
                  ))}

                  {/* 카테고리 미지정 배송 상품 (누락 방지) */}
                  {uncategorizedDelivery.length > 0 && (
                    <section
                      data-target-key="etc"
                      ref={(el) => {
                        sectionRefs.current.etc = el;
                      }}
                      className={sectionScrollMtClass}
                    >
                      {deliverySections.length > 0 && (
                        <h2 className="mb-3 text-base font-bold text-gray-900">기타</h2>
                      )}
                      {renderGrid(uncategorizedDelivery, 'delivery-etc', true)}
                    </section>
                  )}
                </div>
              </section>
            )}
          </div>
        ) : (
          <div className="center-box h-screen-15 flex-col gap-2 text-gray-500">
            <p>등록된 답례품이 없습니다.</p>
          </div>
        )
      ) : (
        <div className="center-box h-screen-15">
          <Loading />
        </div>
      )}
      <GiftCallHelperBox />
      <GiftStoreFooter />
    </div>
  );
};

export default GiftStoreMainPage;
